import { useEffect, useState, useCallback } from "react";
import { api, apiHref } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Factory, Play, CheckCircle2, Printer } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import { localToday } from "@/lib/dates";

const STATUS_COLOR = {
  queued: "border-white text-ink",
  batching: "border-primary-orange text-primary-orange pulse-orange",
  loaded: "border-[#D4D4D8] text-[#D4D4D8]",
  complete: "border-[#71717A] text-[#71717A]",
};

export default function Batching() {
  const today = localToday();
  const [jobs, setJobs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState({ job_id: "", truck_id: "", quantity_m3: 8 });

  const load = useCallback(async () => {
    const [j, t, b] = await Promise.all([api.get(`/jobs?date=${today}`), api.get("/trucks"), api.get("/batches")]);
    setJobs(j.data);
    setTrucks(t.data);
    setBatches(b.data);
  }, [today]);

  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);

  const create = async () => {
    if (!form.job_id || !form.truck_id) return toast.error("Job and truck required");
    try {
      await api.post("/batches", form);
      toast.success("Batch queued");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const start = async (id) => { await api.post(`/batches/${id}/start`); toast.success("Batching started"); load(); };
  const complete = async (id) => {
    // No auto-print — the operator prints on demand via the Docket button below.
    try {
      const res = await api.post(`/batches/${id}/complete`);
      toast.success(`Docket ${res.data.docket?.docket_no} created`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Batch-screen only: once a job's full ordered m³ is delivered it drops out of the
  // picker, and completed loads move out of the active queue into "Completed today".
  const selectableJobs = jobs.filter(
    (j) => !["completed", "cancelled"].includes(j.status) && (Number(j.delivered_m3) || 0) < Number(j.quantity_m3),
  );
  const activeBatches = batches.filter((b) => ["queued", "batching"].includes(b.status));
  const doneBatches = batches.filter((b) => b.status === "complete");

  const selectedJob = jobs.find((j) => j.id === form.job_id);
  const availableTrucks = trucks.filter((t) => t.status === "available" || t.status === "loading");

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="batching-page">
      <div>
        <div className="label-tag">PRODUCTION</div>
        <h1 className="heading text-3xl font-black uppercase">Batching <span className="text-primary-orange">Control</span></h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Batch form */}
        <div className="card-industrial p-5">
          <div className="flex items-center gap-2 mb-4">
            <Factory className="w-4 h-4 text-primary-orange" />
            <div className="label-tag">New Batch</div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="label-tag">Job</Label>
              <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                <SelectTrigger data-testid="batch-job-select" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select job" /></SelectTrigger>
                <SelectContent className="rounded-none bg-surface border-industrial">
                  {selectableJobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.customer_name} · {j.mix_code} · {j.quantity_m3} m³</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Truck</Label>
              <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
                <SelectTrigger data-testid="batch-truck-select" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select truck" /></SelectTrigger>
                <SelectContent className="rounded-none bg-surface border-industrial">
                  {availableTrucks.map((t) => <SelectItem key={t.id} value={t.id}>{t.number} · {t.capacity_m3} m³ · {t.driver_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Quantity (m³)</Label>
              <Input data-testid="batch-qty" type="number" step="0.5" value={form.quantity_m3} onChange={(e) => setForm({ ...form, quantity_m3: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <Button data-testid="batch-create-btn" onClick={create} className="w-full rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Queue Batch</Button>
            {selectedJob && (
              <div className="border border-industrial p-3 mt-3">
                <div className="label-tag mb-1">Mix Preview</div>
                <div className="mono text-sm">{selectedJob.mix_code} · Slump {selectedJob.slump_mm} mm</div>
                <div className="text-xs text-muted-industrial mt-1">Total ordered: {selectedJob.quantity_m3} m³ · Delivered: {selectedJob.delivered_m3} m³</div>
              </div>
            )}
          </div>
        </div>

        {/* Active batches — clears automatically once a load is completed */}
        <div className="space-y-6">
          <div className="card-industrial p-5">
            <div className="label-tag mb-4">Batch Queue</div>
            {activeBatches.length === 0 && <div className="text-sm text-muted-industrial" data-testid="active-queue-empty">No active loads — queue a batch to get started.</div>}
            <div className="space-y-3">
              {activeBatches.map((b) => <BatchRow key={b.id} b={b} onStart={start} onComplete={complete} />)}
            </div>
          </div>

          {doneBatches.length > 0 && (
            <div className="card-industrial p-5" data-testid="completed-batches">
              <div className="label-tag mb-4 text-muted-industrial">Completed today · {doneBatches.length}</div>
              <div className="space-y-3 opacity-90">
                {doneBatches.slice(0, 8).map((b) => <BatchRow key={b.id} b={b} onStart={start} onComplete={complete} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BatchRow({ b, onStart, onComplete }) {
  return (
    <div className="border border-industrial p-3" data-testid={`batch-row-${b.id}`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="mono font-bold">{b.mix_code} · Truck {b.truck_number}</div>
          <div className="text-xs text-muted-industrial">{b.quantity_m3} m³ · {new Date(b.created_at).toLocaleTimeString()}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLOR[b.status]}`}>{b.status}</span>
          {b.status === "complete" && (
            <span data-testid={`tol-${b.id}`} className={`mono text-[9px] uppercase tracking-wider px-2 py-0.5 border ${b.within_tolerance ? "border-white text-ink" : "border-primary-orange text-primary-orange"}`}>
              {b.within_tolerance ? `✓ Within ±${b.tolerance_pct}%` : `⚠ ${b.out_of_tolerance?.length || 0} out of tol`}
            </span>
          )}
        </div>
      </div>
      {b.status === "complete" && Object.keys(b.target_weights || {}).length > 0 && (
        <div className="mt-3 grid grid-cols-4 gap-1 text-[10px] mono">
          {Object.keys(b.target_weights).map((k) => {
            const t = b.target_weights[k];
            const a = b.actual_weights?.[k] ?? t;
            const dev = t ? ((a - t) / t) * 100 : 0;
            const bad = (b.out_of_tolerance || []).includes(k);
            return (
              <div key={k} className={`border ${bad ? "border-primary-orange" : "border-industrial"} p-1`}>
                <div className="uppercase text-muted-industrial">{k}</div>
                <div className={bad ? "text-primary-orange" : ""}>{a.toFixed(0)} / {t.toFixed(0)}</div>
                <div className={`${bad ? "text-primary-orange" : "text-muted-industrial"}`}>{dev >= 0 ? "+" : ""}{dev.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-2 mt-3">
        {b.status === "queued" && <Button size="sm" onClick={() => onStart(b.id)} data-testid={`start-batch-${b.id}`} className="rounded-none bg-surface-2 hover:bg-surface border border-industrial uppercase tracking-wider text-xs"><Play className="w-3 h-3 mr-1" /> Start</Button>}
        {b.status === "batching" && <Button size="sm" onClick={() => onComplete(b.id)} data-testid={`complete-batch-${b.id}`} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-wider text-xs"><CheckCircle2 className="w-3 h-3 mr-1" /> Complete + Docket</Button>}
        {b.status === "complete" && (
          <a href={apiHref(`/dockets/${b.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`print-batch-${b.id}`}>
            <Button size="sm" variant="outline" className="rounded-none border-industrial uppercase tracking-wider text-xs bg-transparent"><Printer className="w-3 h-3 mr-1" /> Print docket</Button>
          </a>
        )}
      </div>
    </div>
  );
}
